import { useState } from 'react';
import { formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, FilterChip, Skeleton } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { useDealHistory } from '../api';
import type { DealRecord, HistoryKind, Tone } from '../types';
import { ParticipantsCard, SideCard } from './bits';
import styles from './record.module.css';

/** Deal-Akte 10 „Verlauf": the filter chips, the timeline grouped by day and the „Kurzfassung" figures. */

const DEFAULT_KINDS: { key: HistoryKind; label: string }[] = [
  { key: 'all', label: 'Alles' },
  { key: 'stages', label: 'Stages' },
  { key: 'units', label: 'Einheiten' },
  { key: 'financing', label: 'Finanzierung' },
  { key: 'documents', label: 'Dokumente' },
  { key: 'appointments', label: 'Termine' },
  { key: 'communication', label: 'Kommunikation' },
];

const dotClass: Record<Tone, string | undefined> = {
  accent: styles.dotAccent,
  success: styles.dotSuccess,
  danger: styles.dotDanger,
  info: styles.dotInfo,
  warning: styles.dotWarning,
  neutral: styles.dotNeutral,
};

export function HistoryTab({ deal }: { deal: DealRecord }) {
  const [kind, setKind] = useState<HistoryKind>('all');
  const history = useDealHistory(deal.id, kind, true);
  const kinds = history.data?.kinds ?? DEFAULT_KINDS;
  const summary = history.data?.summary;

  return (
    <div className={styles.content}>
      <div className={styles.main}>
        <div className={styles.historyFilters} role="group" aria-label="Verlauf filtern">
          {kinds.map((entry) => (
            <FilterChip
              key={entry.key}
              label={entry.label}
              active={kind === entry.key}
              onClick={() => setKind(entry.key)}
            />
          ))}
        </div>
        <section className={styles.card} aria-label="Verlauf">
          {history.isPending ? (
            <div className={styles.skeletonStack}>
              <Skeleton width="30%" height={10} />
              <Skeleton variant="block" height={40} />
              <Skeleton variant="block" height={40} />
            </div>
          ) : null}
          {history.isError ? <Banner tone="danger">{errorMessage(history.error)}</Banner> : null}
          {history.data && history.data.days.length === 0 ? (
            <div className={styles.gridMuted}>Noch keine Einträge.</div>
          ) : null}
          {history.data?.days.map((day) => (
            <div key={day.day}>
              <div className={styles.historyDayLabel}>{day.label}</div>
              {day.entries.map((entry) => (
                <div key={entry.id} className={styles.historyRow}>
                  <span className={styles.historyTime}>{entry.time}</span>
                  <span className={clsx(styles.historyDot, dotClass[entry.tone])} aria-hidden="true" />
                  <span>
                    <div className={styles.historyTitle}>{entry.title}</div>
                    {entry.text ? <div className={styles.historyText}>{entry.text}</div> : null}
                  </span>
                  <span className={styles.historyActor}>{entry.actorShort}</span>
                </div>
              ))}
            </div>
          ))}
        </section>
      </div>
      <div className={styles.side}>
        <SideCard label="Kurzfassung">
          <div className={styles.summaryRows}>
            <div className={styles.summaryRow}>
              <span className={styles.summaryKey}>Deal-Alter</span>
              <span className={styles.summaryValue}>
                {summary ? `${formatInteger(summary.ageDays)} Tage` : '–'}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryKey}>Stage-Wechsel</span>
              <span className={styles.summaryValue}>
                {summary ? formatInteger(summary.stageChanges) : '–'}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryKey}>Einheitenwechsel</span>
              <span className={styles.summaryValue}>
                {summary ? formatInteger(summary.unitChanges) : '–'}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryKey}>Termine</span>
              <span className={styles.summaryValue}>
                {summary
                  ? `${formatInteger(summary.appointments)}${summary.noShows > 0 ? ` · ${formatInteger(summary.noShows)} No Show` : ''}`
                  : '–'}
              </span>
            </div>
          </div>
        </SideCard>
        <ParticipantsCard deal={deal} />
      </div>
    </div>
  );
}
